import React, { useState, useMemo } from 'react';
import { STATUTS_ADHESION, MODES_PAIEMENT, CONTRATS, initials, formatMontant, exportCSV, uid } from '../utils/helpers';

export default function RechercheAvancee({ adherents, etablissements, cotisations, selectedYear, goToAdherent, goToEtab, currentRole, saveAdherent, saveDemande, currentUsername, currentUserEtabsIds }) {
  const [q, setQ] = useState('');
  const [fEtab, setFEtab] = useState('');
  const [fMetier, setFMetier] = useState('');
  const [fContrat, setFContrat] = useState('');
  const [fMode, setFMode] = useState('');
  const [fStatut, setFStatut] = useState('');
  const [view, setView] = useState('liste');
  const [demandeModal, setDemandeModal] = useState(null); // { adh, type: 'rib'|'mode' }
  const [ribForm, setRibForm] = useState({ iban: '', bic: '', banque: '', motif: '' });
  const [modeForm, setModeForm] = useState({ rum: '', iban: '', bic: '', banque: '', jourPrelevement: '5', motif: '' });
  const [demandeOk, setDemandeOk] = useState('');

  const isDelegue = currentRole === 'delegue';

  const yearCots = cotisations[selectedYear] || {};
  const etabName = id => etablissements.find(e => e.id === id)?.nom || '—';

  // Pour le délégué : restreindre aux établissements assignés (si liste non vide)
  const allowedEtabIds = useMemo(() => {
    if (!isDelegue) return null; // null = pas de restriction
    if (!currentUserEtabsIds || currentUserEtabsIds.length === 0) return null; // tous
    return new Set(currentUserEtabsIds);
  }, [isDelegue, currentUserEtabsIds]);

  // Établissements visibles dans le filtre (restreints pour delegue)
  const visibleEtabs = useMemo(() => {
    if (!allowedEtabIds) return etablissements;
    return etablissements.filter(e => allowedEtabIds.has(e.id));
  }, [etablissements, allowedEtabIds]);

  const filtered = useMemo(() => {
    return adherents.filter(adh => {
      if (allowedEtabIds && !allowedEtabIds.has(adh.etablissementId)) return false;
      if (fEtab && adh.etablissementId !== fEtab) return false;
      if (fMetier && !(adh.metier||'').toLowerCase().includes(fMetier.toLowerCase())) return false;
      if (fContrat && adh.contrat !== fContrat) return false;
      if (fMode && adh.modePaiement !== fMode) return false;
      if (fStatut && adh.statut !== fStatut) return false;
      if (q) {
        const txt = (adh.prenom+' '+adh.nom+' '+adh.metier+' '+etabName(adh.etablissementId)+' '+(adh.numero||'')).toLowerCase();
        if (!txt.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [adherents, q, fEtab, fMetier, fContrat, fMode, fStatut, etablissements, allowedEtabIds]); // eslint-disable-line

  const handleIBAN = (v, setter) => {
    const raw = v.replace(/\s/g,'').toUpperCase();
    const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
    setter(f => ({ ...f, iban: formatted }));
  };

  const openDemandeModal = (adh, type) => {
    setDemandeModal({ adh, type });
    setRibForm({ iban: adh.iban || '', bic: adh.bic || '', banque: adh.banque || '', motif: '' });
    setModeForm({ rum: '', iban: adh.iban || '', bic: adh.bic || '', banque: adh.banque || '', jourPrelevement: '5', motif: '' });
    setDemandeOk('');
  };

  const submitDemande = () => {
    if (!demandeModal) return;
    const { adh, type } = demandeModal;
    const isRib  = type === 'rib';
    const form   = isRib ? ribForm : modeForm;
    if (isRib && !form.iban.trim()) return;
    if (!isRib && (!form.iban.trim() || !form.rum.trim())) return;
    const demande = {
      id: uid(),
      type: isRib ? 'changement_rib' : 'changement_mode_paiement',
      statut: 'en_attente',
      createdBy: currentUsername || '',
      dateCreation: new Date().toISOString(),
      adherentId: adh.id,
      nom: adh.nom, prenom: adh.prenom,
      etablissementId: adh.etablissementId,
      etablissementLibre: '',
      ...form,
    };
    saveDemande && saveDemande(demande);
    setDemandeOk(isRib ? 'Demande de changement de RIB soumise.' : 'Demande de passage en prélèvement soumise.');
    setTimeout(() => { setDemandeModal(null); setDemandeOk(''); }, 2000);
  };

  const hasFilter = q || fEtab || fMetier || fContrat || fMode || fStatut;
  const clear = () => { setQ(''); setFEtab(''); setFMetier(''); setFContrat(''); setFMode(''); setFStatut(''); };

  // By metier grouping
  const byMetier = useMemo(() => {
    const g = {};
    filtered.forEach(adh => {
      const m = adh.metier || 'Non renseigné';
      if (!g[m]) g[m] = [];
      g[m].push(adh);
    });
    return Object.entries(g).sort((a,b) => b[1].length - a[1].length);
  }, [filtered]);

  // By etablissement grouping
  const byEtab = useMemo(() => {
    const g = {};
    filtered.forEach(adh => {
      const e = adh.etablissementId || '__none__';
      if (!g[e]) g[e] = [];
      g[e].push(adh);
    });
    return Object.entries(g).sort((a,b) => b[1].length - a[1].length);
  }, [filtered]);

  const handleExport = () => {
    const rows = [['N°','Prénom','Nom','Statut','Établissement','Métier','Contrat','Mode paiement','Montant']];
    filtered.forEach(adh => rows.push([adh.numero,adh.prenom,adh.nom,adh.statut,etabName(adh.etablissementId),adh.metier,adh.contrat,adh.modePaiement,adh.montant]));
    exportCSV(rows, `recherche_sdassp_${selectedYear}.csv`);
  };

  // unique metiers for filter autocomplete
  const metiers = useMemo(() => [...new Set(adherents.map(a=>a.metier).filter(Boolean))].sort(), [adherents]);

  return (
    <div>
      {/* ── Modal demande RIB / mode paiement ── */}
      {demandeModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header">
              <span className="modal-title">
                {demandeModal.type === 'rib' ? 'Changement de RIB' : 'Passage en prélèvement automatique'}
              </span>
              <button className="btn-icon" onClick={() => setDemandeModal(null)}>✕</button>
            </div>
            <div style={{ background:'#f9fafb', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:14 }}>{demandeModal.adh.prenom} {demandeModal.adh.nom}</div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{etabName(demandeModal.adh.etablissementId)}</div>
            </div>
            {demandeOk && <div className="alert alert-success mb-3" style={{ fontSize:13 }}>{demandeOk}</div>}

            {demandeModal.type === 'rib' && (
              <div>
                <div className="form-group">
                  <label className="form-label">Nouveau IBAN *</label>
                  <input className="form-control" value={ribForm.iban}
                    onChange={e => handleIBAN(e.target.value, setRibForm)}
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    style={{ fontFamily:'monospace', letterSpacing:1 }} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">BIC / SWIFT</label>
                    <input className="form-control" value={ribForm.bic}
                      onChange={e => setRibForm(f=>({...f,bic:e.target.value.toUpperCase()}))}
                      placeholder="Ex : AGRIFRPP" style={{ fontFamily:'monospace' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Banque</label>
                    <input className="form-control" value={ribForm.banque}
                      onChange={e => setRibForm(f=>({...f,banque:e.target.value}))}
                      placeholder="Ex : Crédit Agricole" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Motif / Note (optionnel)</label>
                  <input className="form-control" value={ribForm.motif}
                    onChange={e => setRibForm(f=>({...f,motif:e.target.value}))}
                    placeholder="Ex : changement de banque" />
                </div>
              </div>
            )}

            {demandeModal.type === 'mode' && (
              <div>
                <div style={{ fontSize:12, color:'#6b7280', marginBottom:12 }}>
                  Mode actuel : <strong>{demandeModal.adh.modePaiement || '—'}</strong> → Prélèvement automatique
                </div>
                <div className="form-group">
                  <label className="form-label">RUM (Référence Unique de Mandat) *</label>
                  <input className="form-control" value={modeForm.rum}
                    onChange={e => setModeForm(f=>({...f,rum:e.target.value}))}
                    placeholder="SDASSP-2026-XXX-001" style={{ fontFamily:'monospace' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Nouveau IBAN *</label>
                  <input className="form-control" value={modeForm.iban}
                    onChange={e => handleIBAN(e.target.value, setModeForm)}
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    style={{ fontFamily:'monospace', letterSpacing:1 }} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">BIC / SWIFT</label>
                    <input className="form-control" value={modeForm.bic}
                      onChange={e => setModeForm(f=>({...f,bic:e.target.value.toUpperCase()}))}
                      placeholder="AGRIFRPP" style={{ fontFamily:'monospace' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Jour de prélèvement</label>
                    <select className="form-control" value={modeForm.jourPrelevement}
                      onChange={e => setModeForm(f=>({...f,jourPrelevement:e.target.value}))}>
                      {[['1','1er'],['3','3'],['5','5'],['10','10'],['30','30']].map(([v,l]) =>
                        <option key={v} value={v}>{l} du mois</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Motif / Note (optionnel)</label>
                  <input className="form-control" value={modeForm.motif}
                    onChange={e => setModeForm(f=>({...f,motif:e.target.value}))}
                    placeholder="Ex : demande de l'adhérent" />
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDemandeModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={submitDemande}
                disabled={demandeModal.type === 'rib' ? !ribForm.iban.trim() : (!modeForm.iban.trim() || !modeForm.rum.trim())}>
                Soumettre la demande
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Recherche</div>
          <div className="page-subtitle">
            {isDelegue && allowedEtabIds ? `${allowedEtabIds.size} établissement${allowedEtabIds.size > 1 ? 's' : ''} assigné${allowedEtabIds.size > 1 ? 's' : ''} — ` : ''}
            {filtered.length} résultat{filtered.length>1?'s':''} sur {isDelegue && allowedEtabIds ? adherents.filter(a => allowedEtabIds.has(a.etablissementId)).length : adherents.length}
          </div>
        </div>
        {!isDelegue && <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ CSV</button>}
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:10}}>
          <input className="form-control" placeholder="Recherche libre..." value={q} onChange={e=>setQ(e.target.value)} style={{gridColumn:'1/-1'}} />
          <select className="form-control" value={fEtab} onChange={e=>setFEtab(e.target.value)}>
            <option value="">{isDelegue && allowedEtabIds ? 'Mes établissements' : 'Tous établissements'}</option>
            {visibleEtabs.map(e=><option key={e.id} value={e.id}>{e.nom}</option>)}
          </select>
          <select className="form-control" value={fMetier} onChange={e=>setFMetier(e.target.value)}>
            <option value="">Tous métiers</option>
            {metiers.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-control" value={fContrat} onChange={e=>setFContrat(e.target.value)}>
            <option value="">Tous contrats</option>
            {CONTRATS.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {!isDelegue && (
            <select className="form-control" value={fMode} onChange={e=>setFMode(e.target.value)}>
              <option value="">Tous modes</option>
              {MODES_PAIEMENT.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <select className="form-control" value={fStatut} onChange={e=>setFStatut(e.target.value)}>
            <option value="">Tous statuts</option>
            {STATUTS_ADHESION.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          {hasFilter && <button className="btn btn-ghost btn-sm" onClick={clear} style={{alignSelf:'center'}}>✕ Effacer tout</button>}
        </div>
      </div>

      {/* View toggle */}
      <div style={{display:'flex', gap:8, marginBottom:16}}>
        {[['liste','📋 Liste'],['metier','🧑‍⚕️ Par métier'],['etablissement','� Par établissement']].map(([v,l]) => (
          <button key={v} className={`btn btn-sm ${view===v?'btn-primary':'btn-outline'}`} onClick={()=>setView(v)}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state"><div className="empty-state-icon">🔍</div><div className="empty-state-text">Aucun résultat pour ces critères.</div></div>
      )}

      {/* LIST VIEW */}
      {view === 'liste' && filtered.length > 0 && (
        <div>
          {filtered.map(adh => {
            const yearCots = cotisations[selectedYear] || {};
            const collected = Object.values(yearCots[adh.id] || {}).reduce((s,c) => s+(c.status==='paye'?(adh.montant||0):0), 0);
            const mult = adh.periodicite==='Trimestrielle'?4:adh.periodicite==='Semestrielle'?2:adh.periodicite==='Annuelle'?1:12;
            const total = (adh.montant||0)*mult;
            const dotClass = adh.statut==='Actif'?'actif':adh.statut==='Suspendu'?'suspendu':adh.statut==='Inactif'?'inactif':'radie';
            const etab = etablissements.find(e=>e.id===adh.etablissementId);
            return (
              <div key={adh.id} className="member-card">
                <div className="member-card-avatar" onClick={isDelegue ? undefined : ()=>goToAdherent(adh.id)} style={{cursor:isDelegue?'default':'pointer'}}>{initials(adh.prenom,adh.nom)}</div>
                <div className="member-card-info" onClick={isDelegue ? undefined : ()=>goToAdherent(adh.id)} style={{cursor:isDelegue?'default':'pointer'}}>
                  <div className="member-card-name">{adh.prenom} {adh.nom}</div>
                  <div className="member-card-role">{adh.metier||'—'} · {etab?.nom||'—'}</div>
                  <div className="member-card-pay">{adh.contrat} · {adh.modePaiement||'—'}</div>
                </div>
                <div className="member-card-right" style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                  {!isDelegue && (
                    <div className="member-card-amount">{Math.round(collected)}<span style={{fontWeight:400,color:'#aaa',fontSize:11}}>/{Math.round(total)} €</span></div>
                  )}
                  {isDelegue ? (
                    <div style={{display:'flex',gap:4,flexWrap:'wrap',justifyContent:'flex-end'}}>
                      <button className="btn btn-outline btn-xs"
                        style={{fontSize:11}}
                        onClick={e => { e.stopPropagation(); openDemandeModal(adh, 'rib'); }}
                        title="Proposer un changement de RIB">
                        🏦 Changer RIB
                      </button>
                      {adh.modePaiement !== 'Prélèvement auto' && (
                        <button className="btn btn-outline btn-xs"
                          style={{fontSize:11}}
                          onClick={e => { e.stopPropagation(); openDemandeModal(adh, 'mode'); }}
                          title="Proposer le passage en prélèvement automatique">
                          💳 Passer en prélèvement
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {saveAdherent && (
                        <select
                          value={adh.statut}
                          onClick={e => e.stopPropagation()}
                          onChange={e => { e.stopPropagation(); saveAdherent({ ...adh, statut: e.target.value }); }}
                          style={{
                            fontSize: 11, fontWeight: 700, border: '1.5px solid',
                            borderRadius: 20, padding: '3px 8px', cursor: 'pointer', outline: 'none',
                            background: adh.statut==='Actif'?'#dcfce7':adh.statut==='Suspendu'?'#fff7ed':adh.statut==='Inactif'?'#f3f4f6':'#f3e8ff',
                            color: adh.statut==='Actif'?'#166534':adh.statut==='Suspendu'?'#c2410c':adh.statut==='Inactif'?'#6b7280':'#7e22ce',
                            borderColor: adh.statut==='Actif'?'#86efac':adh.statut==='Suspendu'?'#fdba74':adh.statut==='Inactif'?'#d1d5db':'#d8b4fe',
                          }}
                        >
                          {STATUTS_ADHESION.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      )}
                      <button className="btn-icon" onClick={e=>{e.stopPropagation();goToAdherent(adh.id);}} style={{fontSize:14}}>✏️</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* BY METIER VIEW */}
      {view === 'metier' && filtered.length > 0 && (
        <div>
          {byMetier.map(([metier, adhs]) => (
            <div key={metier} className="card mb-3">
              <div className="card-header">
                <div>
                  <span className="card-title">{metier}</span>
                  <span style={{fontSize:12,color:'#888',marginLeft:10}}>{adhs.length} adhérent{adhs.length>1?'s':''}</span>
                </div>
                <div className="flex gap-2" style={{flexWrap:'wrap'}}>
                  {[...new Set(adhs.map(a=>a.contrat))].map(c=>(
                    <span key={c} style={{fontSize:11,background:'#f0f0f0',padding:'2px 8px',borderRadius:10,color:'#555'}}>{c}</span>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {adhs.map(adh=>(
                  <div key={adh.id} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 10px',border:'1px solid #eee',borderRadius:6,cursor:'pointer',background:'#fafafa'}}
                    onClick={()=>goToAdherent(adh.id)}>
                    <div className="adh-avatar" style={{width:26,height:26,fontSize:10}}>{initials(adh.prenom,adh.nom)}</div>
                    <div style={{fontSize:12}}>
                      <div style={{fontWeight:600}}>{adh.prenom} {adh.nom}</div>
                      <div style={{color:'#888',fontSize:11}}>{etabName(adh.etablissementId)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BY ETABLISSEMENT VIEW */}
      {view === 'etablissement' && filtered.length > 0 && (
        <div>
          {byEtab.map(([etabId, adhs]) => {
            const etab = etablissements.find(e=>e.id===etabId);
            const metierGroups = {};
            adhs.forEach(a => {
              const m = a.metier||'Non renseigné';
              if (!metierGroups[m]) metierGroups[m]=0;
              metierGroups[m]++;
            });
            return (
              <div key={etabId} className="card mb-3">
                <div className="card-header">
                  <div>
                    <span className="card-title" style={{cursor:etab?'pointer':'default',color:etab?'#CC0000':'inherit'}} onClick={()=>etab&&goToEtab&&goToEtab(etabId)}>
                      {etab?.nom||'Établissement inconnu'}
                    </span>
                    <span style={{fontSize:12,color:'#888',marginLeft:10}}>{adhs.length} adhérent{adhs.length>1?'s':''}</span>
                  </div>
                  <div style={{fontSize:12,color:'#888'}}>{formatMontant(adhs.reduce((s,a)=>s+(a.montant||0),0))}/mois</div>
                </div>
                {/* Metier breakdown */}
                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
                  {Object.entries(metierGroups).sort((a,b)=>b[1]-a[1]).map(([m,cnt])=>(
                    <span key={m} style={{fontSize:12,background:'#f5f5f5',padding:'3px 10px',borderRadius:10,color:'#555'}}>
                      {m} <strong style={{color:'#CC0000'}}>×{cnt}</strong>
                    </span>
                  ))}
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {adhs.map(adh=>(
                    <div key={adh.id} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 8px',border:'1px solid #eee',borderRadius:6,cursor:'pointer',background:'#fafafa'}}
                      onClick={()=>goToAdherent(adh.id)}>
                      <div className="adh-avatar" style={{width:24,height:24,fontSize:10}}>{initials(adh.prenom,adh.nom)}</div>
                      <div style={{fontSize:12,fontWeight:500}}>{adh.prenom} {adh.nom}</div>
                      <span className={`badge badge-${adh.statut==='Actif'?'actif':adh.statut==='Suspendu'?'suspendu':adh.statut==='Inactif'?'inactif':'radie'}`} style={{fontSize:10}}>{adh.statut}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
